/**
 * NetworkHub — one Settings tab that gathers the network-related surfaces behind a
 * segmented control (not tabs-under-tabs): Settings, Queue & throughput (admin), Blocks &
 * proxies (admin), Trace (admin), Site Parsers (admin), and Usage (admin). Consolidating them frees up the top-level Settings
 * tab bar. The active sub-view is kept in the URL as
 * `?tab=network&sub=<settings|activity|protection|trace|parsers|usage>` so it's linkable + refreshable.
 */
import { useSearchParams } from 'react-router-dom';
import { SubTabs } from '../components/common/Tabs';
import { useAuth } from '../contexts/AuthContext';
import NetworkSettingsPage from './NetworkSettingsPage';
import NetworkRequestReport from './NetworkRequestReport';
import NetworkActivity from './NetworkActivity';
import NetworkProtection from './NetworkProtection';
import NetworkTrace from './NetworkTrace';
import SiteParsers from './SiteParsers';

type NetworkSub = 'settings' | 'activity' | 'protection' | 'trace' | 'parsers' | 'usage';
const SUB_KEYS: NetworkSub[] = ['settings', 'activity', 'protection', 'trace', 'parsers', 'usage'];
const SUB_CONFIG: { key: NetworkSub; label: string; adminOnly?: boolean }[] = [
  { key: 'settings', label: 'Settings' },
  // What the network is doing, to watch rather than set. Admin-only, as it was
  // on the Settings page.
  { key: 'activity', label: 'Queue & throughput', adminOnly: true },
  // Which sites block us, through which proxies, and what fetching does about it.
  { key: 'protection', label: 'Blocks & proxies', adminOnly: true },
  // One keyword or url followed through every step, dry or for real.
  { key: 'trace', label: 'Trace', adminOnly: true },
  { key: 'parsers', label: 'Site Parsers', adminOnly: true },
  // Admin-only, like the other usage panels: what the machinery consumed is not
  // something the product asks a customer to think about.
  { key: 'usage', label: 'Usage', adminOnly: true },
];

export default function NetworkHub({
  initialSub = 'settings',
}: {
  initialSub?: NetworkSub;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const raw = searchParams.get('sub');
  const requested: NetworkSub = SUB_KEYS.includes(raw as NetworkSub)
    ? (raw as NetworkSub)
    : initialSub;
  // A non-admin can't reach the admin-only subs — fall back to Settings.
  const adminOnlySubs = new Set(
    SUB_CONFIG.filter((s) => s.adminOnly).map((s) => s.key),
  );
  const sub: NetworkSub =
    adminOnlySubs.has(requested) && !isAdmin ? 'settings' : requested;

  const setSub = (s: NetworkSub) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'network');
    if (s === 'settings') params.delete('sub');
    else params.set('sub', s);
    // `parser` addresses an open drawer inside the Site Parsers sub; leaving
    // that sub should not leave a stale drawer id in the URL.
    if (s !== 'parsers') params.delete('parser');
    setSearchParams(params, { replace: true });
  };

  const visible = SUB_CONFIG.filter((s) => !s.adminOnly || isAdmin);

  return (
    <div>
      {/* The Site Parsers models widget lives in the parser drawer, not here —
          the drawer is where the work (and the Rebuild button) is. */}
      <SubTabs
        tabs={visible}
        active={sub}
        onChange={setSub}
        description="How the crawler reaches the web, the per-site extractors it uses, and what it has fetched."
      />

      {sub === 'settings' && <NetworkSettingsPage />}
      {sub === 'activity' && isAdmin && <NetworkActivity />}
      {sub === 'protection' && isAdmin && <NetworkProtection />}
      {sub === 'trace' && isAdmin && <NetworkTrace />}
      {sub === 'parsers' && isAdmin && <SiteParsers />}
      {sub === 'usage' && isAdmin && <NetworkRequestReport />}
    </div>
  );
}
